import type {
  CommentsSortUi,
  IGetCommentResponse,
  IProjectAuthorShort,
  IProjectCommentCountersByType,
} from './projects'

export type SocialOrderStatus = 'draft' | 'published'

export interface ISocialOrderViewerPermissions {
  is_owner: boolean
  can_edit_content: boolean
  can_delete_social_order: boolean
  can_comment: boolean
  restriction_level?: 'full' | 'comments' | null
}

export type SocialOrderUserRestrictionLevel = 'full' | 'comments'

export interface ISocialOrderModerationUserCandidate extends IProjectAuthorShort {
  /** Stable id (prefer over user.id when stub has id: null). */
  user_id: number
  restrictable: boolean
  restrictable_reason?: string | null
}

export interface ISocialOrderModerationUserCandidatesResponse {
  items: ISocialOrderModerationUserCandidate[]
  total: number
}

export interface ISocialOrderUserRestrictionResource {
  user: IProjectAuthorShort
  /** Stable id for DELETE (even when user stub has id: null). */
  user_id: number
  level: SocialOrderUserRestrictionLevel
  reason: string | null
  created_at: string
  created_by: IProjectAuthorShort
}

export interface ISocialOrderViewerResource {
  is_followed: boolean
  is_liked?: boolean
  permissions?: ISocialOrderViewerPermissions
}

export interface ISocialOrderCountersResource {
  likes: number
  views: number
  subscribers: number
  /** Published projects linked via social_order_id — present on detail/show; list may omit (normalize → 0). */
  linked_projects?: number
  comments: number | IProjectCommentCountersByType
}

export interface ISocialOrderResource {
  id: number
  author_id: number
  title: string
  slug: string
  status: SocialOrderStatus | string
  description: string
  focus: string[]
  is_completed: boolean
  problem: string | null
  completion_description: string | null
  completion_resource_url: string | null
  image: string | null
  /** Detail only; short list does not include gallery. */
  image_gallery?: string[] | null
  author?: IProjectAuthorShort | null
  likes_preview?: IProjectAuthorShort[] | null
  is_followed?: boolean
  viewer?: ISocialOrderViewerResource | null
  counters: ISocialOrderCountersResource
  published_at: string | null
  created_at: string
  updated_at: string
}

/** Legacy-поля для страниц create/edit до миграции на ISocialOrderResource (шаги 9–10). */
export interface ISocialOrderLegacyCompat {
  authorId: number
  authorFirstName: string
  authorLastName: string
  authorOtch: string
  authorAvatarUrl: string
  authorCharacteristics: string[]
  authorIsOnline: boolean
  createdAt: string
  updatedAt: string
  views: number
  likes: number
  comments: number
}

export type ISocialOrderDetail = ISocialOrderResource & ISocialOrderLegacyCompat

export interface ISocialOrderListItem extends ISocialOrderResource {
  counters: ISocialOrderCountersResource & { comments: number }
}

export type TSocialOrdersListResponse = ISocialOrderListItem[]

export type TGetSocialOrdersQueryParams = {
  query?: string
  focus?: string[]
  sort_by?: 'published_at' | 'created_at'
  sort_direction?: 'asc' | 'desc'
}

export type TGetSocialOrdersCursorQueryParams = TGetSocialOrdersQueryParams & {
  cursor?: string | null
  limit?: number
}

export type TSocialOrdersCursorPage = {
  items: TSocialOrdersListResponse
  meta: { next_cursor: string | null }
}

export type TCreateSocialOrderBody = {
  title: string
  description: string
  focus: string[]
  problem: string
  status?: SocialOrderStatus
  is_completed?: boolean
  completion_description?: string | null
  completion_resource_url?: string | null
}

export type TUpdateSocialOrderBody = Partial<TCreateSocialOrderBody>

export type GetSocialOrderCommentsQueryArg = {
  socialOrderId: number
  type: string
  parentId?: number
  limit?: number
  cursor?: string | null
  sort?: CommentsSortUi
}

export type TGetSocialOrderCommentsResponse = IGetCommentResponse[]

export type TSocialOrderCommentThreadPage = {
  items: IGetCommentResponse[]
  nextCursor: string | null
}

export type GetSocialOrderCommentThreadPageArg = {
  socialOrderId: number
  type: string
  parentId: number
  cursor?: string | null
  limit?: number
}

export interface ISocialOrderLikeUserResource extends IProjectAuthorShort {
  name?: string | null
}

export function resolveSocialOrderCommentsTotal(counters: ISocialOrderCountersResource['comments']): number {
  if (typeof counters === 'number') return counters
  if (counters && typeof counters === 'object') return counters.total ?? 0
  return 0
}
